import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { useTelemetry } from './useTelemetry'

export type UsePageViewOptions = {
  /** Custom page name override */
  pageName?: string
  /** Additional attributes to include */
  attributes?: Record<string, string>
  /** Skip tracking for this page */
  skip?: boolean
}

/**
 * Hook to automatically track page views on route changes
 *
 * Uses react-router-dom's useLocation to detect navigation.
 * Tracks page views to OpenTelemetry as metrics and spans.
 *
 * @param options - Optional configuration
 *
 * @example
 * ```tsx
 * // Basic usage - track all page views
 * function App() {
 *   usePageView()
 *   return <Routes>...</Routes>
 * }
 *
 * // With custom page name
 * function ProductPage() {
 *   usePageView({ pageName: 'product_detail' })
 *   return <div>Product details</div>
 * }
 *
 * // With additional attributes
 * function CategoryPage({ categoryId }) {
 *   usePageView({
 *     pageName: 'category',
 *     attributes: { category_id: categoryId }
 *   })
 *   return <div>Category</div>
 * }
 * ```
 */
export function usePageView(options: UsePageViewOptions = {}) {
  const telemetry = useTelemetry()
  const location = useLocation()
  const lastPathRef = useRef<string>('')

  useEffect(() => {
    if (options.skip || !telemetry) return

    // Avoid duplicate tracking for same path
    const currentPath = location.pathname + location.search
    if (currentPath === lastPathRef.current) return
    lastPathRef.current = currentPath

    const pageName = options.pageName || location.pathname
    const attributes: Record<string, string> = {
      path: location.pathname,
      search: location.search,
      hash: location.hash,
      ...options.attributes,
    }

    // Add referrer on first page load
    if (document.referrer && !lastPathRef.current) {
      attributes.referrer = document.referrer
    }

    telemetry.trackPageView(pageName, attributes)
  }, [location.pathname, location.search, location.hash, options.pageName, options.skip, options.attributes, telemetry])
}

/**
 * Hook to manually track a page view
 *
 * Unlike usePageView, this doesn't auto-track on route changes.
 * Useful for single-page apps or custom tracking scenarios.
 *
 * @returns Function to track a page view
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const trackPageView = useTrackPageView()
 *
 *   useEffect(() => {
 *     trackPageView('custom_page', { section: 'main' })
 *   }, [])
 * }
 * ```
 */
export function useTrackPageView() {
  const telemetry = useTelemetry()

  return (pageName: string, attributes?: Record<string, string>) => {
    telemetry?.trackPageView(pageName, attributes)
  }
}
